import { useSchedulingAgent } from '@cobliteam/ai/src/hooks/useSchedulingAgent';
import { Analytics } from '@cobliteam/analytics';
import Button from '@cobliteam/colib-ui/Button';
import Typography from '@cobliteam/colib-ui/Typography';
import useCrm from '@cobliteam/hooks/src/useEntity/useCrm';
import { useTranslation } from '@cobliteam/i18n';
import { CalendarClock, Sparkles } from 'lucide-react';

interface InstallationStepBodyProps {
  fleetId?: string;
  /** Closes the guided modal when launching the AI scheduling agent (a separate floating chat). */
  onLaunch?: () => void;
}

/**
 * Devices/installation step: physical install can't be done inline, so we list pending scheduling
 * tickets and let the user kick off the AI scheduling agent for each one.
 */
const InstallationStepBody = ({ fleetId, onLaunch }: InstallationStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { data, isLoading } = useCrm().useSchedulingTickets();
  const schedulingAgent = useSchedulingAgent();
  const tickets = data?.tickets ?? [];

  const handleSchedule = (ticketId?: string) => {
    Analytics.track('BasicValueModalScheduleWithAiClicked', { fleetId, ticketId });
    onLaunch?.();
    schedulingAgent.openWithPrompt(ticketId ? t('inline.installation_specific_prompt', { ticketId }) : t('inline.installation_generic_prompt'), 'onboarding');
  };

  // While the query is in flight, show nothing rather than the title over an empty list.
  if (isLoading) return null;

  if (tickets.length === 0) {
    return (
      <div className='flex flex-col items-start gap-3'>
        <Typography variant='caption' className='gap-1 break-words text-foreground text-muted-foreground'>
          {t('inline.installation_empty')}
        </Typography>
        <Button variant='outline' size='sm' onClick={() => handleSchedule()}>
          <Sparkles size={14} />
          {t('inline.talk_to_assistant')}
        </Button>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <Typography variant='caption' className='gap-1 break-words font-medium text-foreground text-muted-foreground'>
        {t('inline.installation_pending')}
      </Typography>
      <div className='flex max-h-64 flex-col gap-2 overflow-y-auto'>
        {tickets.map((ticket) => {
          const plate = ticket.installationInformation?.vehicleInformation?.plack;
          return (
            <div key={ticket.id} className='flex items-center justify-between gap-3 rounded-lg border border-border border-solid p-3'>
              <span className='flex min-w-0 items-center gap-2'>
                <CalendarClock size={16} className='shrink-0 text-muted-foreground' aria-hidden />
                <Typography variant='caption' className='gap-1 truncate break-words font-medium text-foreground'>
                  {plate || `#${ticket.id}`}
                </Typography>
              </span>
              <Button variant='outline' size='sm' onClick={() => handleSchedule(ticket.id)}>
                <Sparkles size={14} />
                {t('inline.schedule_with_ai')}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InstallationStepBody;
